"use client";

import { motion, AnimatePresence } from "framer-motion";
import type { FC } from "react";
import { springCursor, springSnappy } from "./motion-tokens";

interface AnimatedCursorProps {
  x: number;
  y: number;
  visible?: boolean;
  clicking?: boolean;
  label?: string;
  color?: string;
}

/**
 * Calculates cursor position to center on a target element,
 * relative to a container element.
 */
export function getCursorTarget(
  targetId: string,
  containerId: string,
): { x: number; y: number } | null {
  const target = document.getElementById(targetId);
  const container = document.getElementById(containerId);
  if (!target || !container) return null;

  const targetRect = target.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return {
    x: targetRect.left - containerRect.left + targetRect.width / 2,
    y: targetRect.top - containerRect.top + targetRect.height / 2,
  };
}

export const AnimatedCursor: FC<AnimatedCursorProps> = ({
  x,
  y,
  visible = true,
  clicking = false,
  label,
  color = "#007AFF",
}) => {
  if (!visible) return null;

  return (
    <motion.div
      className="absolute top-0 left-0 pointer-events-none z-50"
      initial={false}
      animate={{ x, y, opacity: 1, scale: 1 }}
      transition={springCursor}
      style={{ width: 28, height: 28 }}
    >
      <AnimatePresence>
        {clicking && (
          <motion.span
            key="ripple"
            className="absolute left-1.5 top-1.5 rounded-full"
            style={{ backgroundColor: color }}
            initial={{ width: 10, height: 10, opacity: 0.35 }}
            animate={{ width: 36, height: 36, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          />
        )}
      </AnimatePresence>

      <motion.svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        animate={{ scale: clicking ? 0.82 : 1, rotate: clicking ? -6 : 0 }}
        transition={springSnappy}
      >
        <path
          d="M5 3L19 12L12 13L9 20L5 3Z"
          fill={color}
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </motion.svg>

      <AnimatePresence>
        {label && (
          <motion.span
            key={label}
            className="absolute left-5 top-4 text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap"
            style={{ backgroundColor: color, color: "white" }}
            initial={{ opacity: 0, x: -4, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -4, scale: 0.9 }}
            transition={{ duration: 0.2 }}
          >
            {label}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
